import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, ChevronDown, Send, ArrowRight, Star } from 'lucide-react';
import { SiteSettings } from '../types';

const faqData = [
  {
    question: "Do you offer private viewings?",
    answer: "Yes. We offer private appointments in our Manila atelier or virtual consultations with our senior gemologists. Please select 'Private Appointment' in the contact form."
  },
  {
    question: "How are your gemstones authenticated?",
    answer: "Every piece comes with a GIA or AGS certificate. Our in-house experts verify provenance and quality before any item enters our curated collection."
  },
  {
    question: "What is your shipping policy?",
    answer: "We provide fully insured, armored courier shipping within the Philippines. International shipping is handled via secure logistics partners with customs handling included."
  }
];

interface ContactProps {
    settings?: SiteSettings;
}

// API configuration
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://sparklinggems.shop/api'
  : 'http://localhost/sparkling-gems-backend-php/api';

const Contact: React.FC<ContactProps> = ({ settings }) => {
  const [activeAccordion, setActiveAccordion] = useState<number | null>(0);
  const [formState, setFormState] = useState({ 
    name: '', 
    email: '', 
    subject: 'General Inquiry', 
    message: '' 
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use settings or fallbacks
  const email = settings?.email || 'rsgleamofgolds@gmail.com';
  const phone = settings?.phone || '0945 392 8946';
  const address = settings?.address || '2F, San Antonio Building, Corumi, Quezon City, 1012 Metro Manila';
  const hours = settings?.hours || 'Mon-Sun: 9am - 8pm';

  const toggleAccordion = (index: number) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Send to your backend API
      const response = await fetch(`${API_BASE_URL}/contact/send.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formState,
          recipient: email // Use the email from settings
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIsSent(true);
        setFormState({ name: '', email: '', subject: 'General Inquiry', message: '' });
      } else {
        setError(data.error || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('Connection error. Please try again later.');
      console.error('Contact form error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative bg-stone-50 pt-48 pb-0">
      {/* Texture Overlay */}
      <div className="absolute inset-0 bg-noise opacity-40 pointer-events-none"></div>

      {/* Header Section */}
      <div className="max-w-7xl mx-auto px-6 mb-20 relative z-10 text-center">
        <span className="text-gold-600 text-xs tracking-[0.3em] uppercase font-bold mb-4 block animate-fade-in">
          Concierge Services
        </span>
        <h2 className="font-serif text-5xl md:text-6xl text-stone-900 mb-6">
          Bespoke <span className="italic text-gold-600">Inquiries</span>
        </h2>
        <p className="text-stone-600 font-light max-w-2xl mx-auto leading-relaxed">
          Whether you seek a rare gemstone, require a private viewing, or have questions about an heirloom, our dedicated team is at your disposal.
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          
          {/* LEFT COLUMN: Contact Form */}
          <div className="bg-white p-8 md:p-12 shadow-2xl shadow-stone-200/50 border border-stone-100 relative overflow-hidden">
            {/* Decorative decorative element */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gold-300 via-gold-500 to-gold-300"></div>
            
            {error && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-sm">
                {error}
              </div>
            )}
            
            {!isSent ? (
              <form onSubmit={handleSubmit} className="space-y-8 relative z-10">
                <div className="space-y-6">
                  <div className="group relative">
                    <input 
                      type="text" 
                      required
                      value={formState.name}
                      onChange={(e) => setFormState({...formState, name: e.target.value})}
                      className="w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-gold-600 transition-colors peer placeholder-transparent"
                      placeholder="Your Name"
                      id="name"
                    />
                    <label 
                      htmlFor="name" 
                      className="absolute left-0 -top-3.5 text-xs text-gold-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-gold-600 peer-focus:text-xs uppercase tracking-wider"
                    >
                      Your Name
                    </label>
                  </div>

                  <div className="group relative">
                    <input 
                      type="email" 
                      required
                      value={formState.email}
                      onChange={(e) => setFormState({...formState, email: e.target.value})}
                      className="w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-gold-600 transition-colors peer placeholder-transparent"
                      placeholder="Email Address"
                      id="email"
                    />
                    <label 
                      htmlFor="email" 
                      className="absolute left-0 -top-3.5 text-xs text-gold-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-gold-600 peer-focus:text-xs uppercase tracking-wider"
                    >
                      Email Address
                    </label>
                  </div>

                  <div className="group relative">
                    <select 
                      value={formState.subject}
                      onChange={(e) => setFormState({...formState, subject: e.target.value})}
                      className="w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-gold-600 transition-colors appearance-none cursor-pointer"
                    >
                      <option>General Inquiry</option>
                      <option>Private Appointment</option>
                      <option>Product Authentication</option>
                      <option>Press & Media</option>
                    </select>
                    <label className="absolute left-0 -top-3.5 text-xs text-gold-600 uppercase tracking-wider">
                      Subject
                    </label>
                    <ChevronDown className="absolute right-0 top-3 text-stone-400 pointer-events-none" size={16} />
                  </div>

                  <div className="group relative">
                    <textarea 
                      rows={4}
                      required
                      value={formState.message}
                      onChange={(e) => setFormState({...formState, message: e.target.value})}
                      className="w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-gold-600 transition-colors peer placeholder-transparent resize-none"
                      placeholder="Message"
                      id="message"
                    ></textarea>
                    <label 
                      htmlFor="message" 
                      className="absolute left-0 -top-3.5 text-xs text-gold-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-gold-600 peer-focus:text-xs uppercase tracking-wider"
                    >
                      How may we assist you?
                    </label>
                  </div>
                </div>

                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-stone-900 text-white py-4 px-8 uppercase tracking-[0.2em] text-xs hover:bg-gold-600 transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed group"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  {!isSubmitting && <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />}
                </button>
              </form>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center py-20 animate-fade-in">
                <div className="w-16 h-16 bg-gold-100 rounded-full flex items-center justify-center mb-6 text-gold-600">
                  <Send size={24} />
                </div>
                <h3 className="font-serif text-2xl mb-2">Message Received</h3>
                <p className="text-stone-500 font-light text-sm max-w-xs">
                  Thank you for contacting Sparkling Gems. A concierge will respond to your inquiry within 24 hours.
                </p>
                <button 
                  onClick={() => setIsSent(false)} 
                  className="mt-8 text-xs uppercase tracking-widest text-stone-900 border-b border-stone-900 pb-1 hover:text-gold-600 hover:border-gold-600 transition-colors"
                >
                  Send another message
                </button>
              </div>
            )}
          </div>

          {/* RIGHT COLUMN: Info & FAQ */}
          <div className="flex flex-col justify-between">
            
            {/* Contact Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <div className="bg-white p-6 border border-stone-100 shadow-sm hover:shadow-md transition-shadow group cursor-default">
                <Phone className="text-gold-500 mb-4 group-hover:scale-110 transition-transform" size={24} strokeWidth={1.5} />
                <h4 className="font-serif text-lg mb-2">Concierge</h4>
                <div className="text-xs text-stone-500 leading-relaxed">
                  <p>{phone}</p>
                  <p className="break-all">{email}</p>
                  <p className="mt-2">{hours}</p>
                </div>
              </div>
              <div className="bg-white p-6 border border-stone-100 shadow-sm hover:shadow-md transition-shadow group cursor-default">
                <MapPin className="text-gold-500 mb-4 group-hover:scale-110 transition-transform" size={24} strokeWidth={1.5} />
                <h4 className="font-serif text-lg mb-2">The Atelier</h4>
                <div className="text-xs text-stone-500 leading-relaxed">
                  <p className="whitespace-pre-wrap">{address}</p>
                  <p className="mt-2">By Appointment Only</p>
                </div>
              </div>
            </div>

            {/* FAQ Accordion */}
            <div className="bg-white/60 backdrop-blur-sm border border-white p-8 rounded-sm">
              <h3 className="text-sm font-bold uppercase tracking-widest text-stone-900 mb-6">Client Services</h3>
              <div className="divide-y divide-stone-200">
                {faqData.map((item, index) => (
                  <div key={index} className="py-4">
                    <button 
                      onClick={() => toggleAccordion(index)}
                      className="w-full flex justify-between items-center text-left group"
                    >
                      <span className={`font-serif text-lg transition-colors ${activeAccordion === index ? 'text-gold-600' : 'text-stone-800 group-hover:text-gold-600'}`}>
                        {item.question}
                      </span>
                      <ChevronDown 
                        size={16} 
                        className={`text-stone-400 transition-transform duration-300 ${activeAccordion === index ? 'rotate-180 text-gold-600' : ''}`} 
                      />
                    </button>
                    <div 
                      className={`overflow-hidden transition-all duration-500 ease-in-out ${
                        activeAccordion === index ? 'max-h-32 opacity-100 mt-3' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <p className="text-stone-500 text-xs leading-relaxed font-light pr-8">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Trust Badge */}
            <div className="mt-8 flex items-center gap-4 opacity-70">
              <div className="flex text-gold-500">
                {[1,2,3,4,5].map(i => <Star key={i} size={14} fill="currentColor" />)}
              </div>
              <span className="text-[10px] uppercase tracking-widest text-stone-500">
                Rated 5.0 for Service Excellence
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Styled Map Section */}
      <div id="location-map" className="relative w-full h-96 mt-24 group overflow-hidden">
        {/* Grayscale filter overlay */}
        <div className="absolute inset-0 bg-stone-900/10 z-10 pointer-events-none mix-blend-saturation"></div>
        <div className="absolute inset-0 z-20 pointer-events-none flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-stone-900/20 backdrop-blur-[1px]">
           <a 
             href="https://www.google.com/maps/place/Sparkling+Gems+Corp/@14.6399511,121.009818,1091m/data=!3m2!1e3!4b1!4m6!3m5!1s0x3397b7541c9ed635:0x1e8f258cec07a7a1!8m2!3d14.6399511!4d121.009818!16s%2Fg%2F11yz7q5q2r?entry=ttu&g_ep=EgoyMDI2MDIxMS4wIKXMDSoASAFQAw%3D%3D"
             target="_blank"
             rel="noopener noreferrer"
             className="pointer-events-auto bg-white text-stone-900 px-6 py-3 uppercase text-xs tracking-widest font-bold shadow-xl transform translate-y-4 group-hover:translate-y-0 transition-all hover:bg-gold-600 hover:text-white"
           >
              Open in Google Maps
           </a>
        </div>
        
        <iframe 
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3860.203874312389!2d121.009818!3d14.6399511!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3397b7541c9ed635%3A0x1e8f258cec07a7a1!2sSparkling%20Gems%20Corp!5e0!3m2!1sen!2sph!4v1709228955821!5m2!1sen!2sph" 
          width="100%" 
          height="100%" 
          style={{ border: 0, filter: 'grayscale(100%) contrast(1.2)' }} 
          allowFullScreen={false} 
          loading="lazy" 
          referrerPolicy="no-referrer-when-downgrade"
          className="w-full h-full"
          title="Sparkling Gems Location"
        ></iframe>
      </div>
    </section>
  );
};

export default Contact;